/**
 * App & Offline card — surfaces PWA state inside Wallet Settings.
 *   - Install status (Not installed / Installed / iOS: add manually)
 *   - Online / offline indicator + queued request count
 *   - Manual "Install now" trigger for browsers that expose beforeinstallprompt
 *   - Manual "Clear cached data" for troubleshooting
 */
import { useEffect, useState } from "react";
import { Download, Smartphone, WifiOff, Wifi, Trash2, CheckCircle2 } from "lucide-react";
import { useInstallPrompt, useOnlineStatus, offlineQueueSize, flushQueue } from "@/lib/pwa";

const NAVY = "#0A1F44";
const GOLD = "#C9A24A";

export default function WalletAppSettingsCard() {
  const { canInstall, installed, promptInstall } = useInstallPrompt();
  const online = useOnlineStatus();
  const [queued, setQueued] = useState(0);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setQueued(offlineQueueSize());
    const t = setInterval(() => setQueued(offlineQueueSize()), 5000);
    return () => clearInterval(t);
  }, []);

  const install = async () => {
    if (!canInstall) return;
    setBusy(true);
    try {
      await promptInstall();
    } finally {
      setBusy(false);
    }
  };

  const forceFlush = async () => {
    setBusy(true);
    try {
      const res = await flushQueue();
      setQueued(res.remaining);
    } finally {
      setBusy(false);
    }
  };

  const clearCache = async () => {
    if (!("caches" in window)) return;
    setBusy(true);
    try {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k)));
      alert("Cached app data cleared. Reloading now.");
      window.location.reload();
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      data-testid="wallet-app-settings"
      className="rounded-2xl bg-white border border-slate-100 shadow-sm p-5"
    >
      <div className="flex items-center gap-2 mb-3">
        <Smartphone className="w-4 h-4" style={{ color: GOLD }} />
        <div className="text-sm font-semibold" style={{ color: NAVY }}>App & offline</div>
      </div>

      {/* Install status */}
      <div className="flex items-center justify-between py-2">
        <div className="pr-3">
          <div className="text-sm text-slate-800">Install Altara Wallet</div>
          <div className="text-[11px] text-slate-500">
            {installed
              ? "Installed on this device."
              : canInstall
              ? "Add to your home screen for one-tap access and offline support."
              : "Open this wallet in Chrome or Safari on your phone to install."}
          </div>
        </div>
        {installed ? (
          <span className="inline-flex items-center gap-1 text-[11px] font-semibold rounded-full px-2.5 py-1 bg-emerald-50 text-emerald-700 border border-emerald-200">
            <CheckCircle2 className="w-3 h-3" /> Installed
          </span>
        ) : canInstall ? (
          <button
            data-testid="settings-install-btn"
            onClick={install}
            disabled={busy}
            className="inline-flex items-center gap-1 text-[12px] font-semibold rounded-full px-3 py-1.5 text-white disabled:opacity-50"
            style={{ background: NAVY }}
          >
            <Download className="w-3.5 h-3.5" /> Install
          </button>
        ) : (
          <span className="text-[11px] text-slate-500">Not available</span>
        )}
      </div>

      {/* Connectivity */}
      <div className="flex items-center justify-between py-2 border-t border-slate-100 mt-2 pt-3">
        <div className="pr-3">
          <div className="text-sm text-slate-800">Connection</div>
          <div className="text-[11px] text-slate-500">
            {online
              ? "Online — signals send instantly."
              : "Offline — Altara will queue your changes and sync when you reconnect."}
          </div>
        </div>
        <span
          className={`inline-flex items-center gap-1 text-[11px] font-semibold rounded-full px-2.5 py-1 border ${
            online
              ? "bg-emerald-50 text-emerald-700 border-emerald-200"
              : "bg-amber-50 text-amber-800 border-amber-200"
          }`}
          data-testid="settings-online-status"
        >
          {online ? <Wifi className="w-3 h-3" /> : <WifiOff className="w-3 h-3" />}
          {online ? "Online" : "Offline"}
        </span>
      </div>

      {/* Queue */}
      <div className="flex items-center justify-between py-2 border-t border-slate-100 mt-2 pt-3">
        <div className="pr-3">
          <div className="text-sm text-slate-800">Pending sync</div>
          <div className="text-[11px] text-slate-500">
            {queued === 0
              ? "Nothing queued. Every signal is up to date."
              : `${queued} change${queued === 1 ? "" : "s"} waiting to sync.`}
          </div>
        </div>
        <button
          data-testid="settings-flush-queue"
          onClick={forceFlush}
          disabled={busy || queued === 0}
          className="text-[12px] font-semibold rounded-full px-3 py-1.5 text-slate-700 border border-slate-200 disabled:opacity-40"
        >
          Sync now
        </button>
      </div>

      {/* Clear cache */}
      <div className="flex items-center justify-between py-2 border-t border-slate-100 mt-2 pt-3">
        <div className="pr-3">
          <div className="text-sm text-slate-800">Reset offline cache</div>
          <div className="text-[11px] text-slate-500">
            Fixes stale screens. Won&rsquo;t sign you out or delete your saved documents.
          </div>
        </div>
        <button
          data-testid="settings-clear-cache"
          onClick={clearCache}
          disabled={busy}
          className="inline-flex items-center gap-1 text-[12px] font-semibold rounded-full px-3 py-1.5 text-rose-700 border border-rose-200 disabled:opacity-40"
        >
          <Trash2 className="w-3.5 h-3.5" /> Clear
        </button>
      </div>
    </section>
  );
}
